
import { MetaMetrics, MetricsFetchConfig } from '@/types/metaMetrics';
import { createInitialMetrics, distributeValue } from '@/utils/metricUtils';
import { enforceRequestInterval, trackSuccessfulRequest, handleRateLimitError } from '@/utils/metaApiRateLimiting';

// Add cache for metrics data to reduce API calls
const metricsCache: Map<string, {data: MetaMetrics, timestamp: number}> = new Map();
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes cache lifetime

/**
 * Fetches metrics data from the Top Ads system and converts it to the format expected by the dashboard
 */
export async function fetchMetricsData({
  accessToken,
  selectedCampaignId,
  dateRange
}: MetricsFetchConfig): Promise<MetaMetrics> {
  if (!accessToken) {
    console.log('No access token provided for metrics fetch');
    return createInitialMetrics();
  }

  // Create a cache key from the parameters
  const cacheKey = `${selectedCampaignId || 'all'}_${dateRange?.startDate || 'default'}_${dateRange?.endDate || 'default'}`;
  const now = Date.now();
  const cachedEntry = metricsCache.get(cacheKey);
  
  // Check if we have a valid cache entry
  if (cachedEntry && now - cachedEntry.timestamp < CACHE_TTL) {
    console.log('Using cached metrics data for', cacheKey);
    return cachedEntry.data;
  }

  try {
    // Apply rate limiting before API call
    await enforceRequestInterval('meta_metrics', 900); // 15 minutes (900 seconds) interval
    
    console.log('Fetching metrics data for dashboard');
    
    // Get ad data from localStorage where the TopAds component stores its data
    const topAdsData = localStorage.getItem('top_ads_data');
    let topAds = [];
    
    if (topAdsData) {
      try {
        topAds = JSON.parse(topAdsData);
        console.log('Found stored top ads data:', topAds.length, 'ads');
      } catch (e) {
        console.error('Error parsing top ads data:', e);
      }
    }
    
    // Find the selected campaign in top ads data
    let selectedAd = null;
    if (selectedCampaignId && topAds.length > 0) {
      selectedAd = topAds.find((ad: any) => ad.id === selectedCampaignId);
    }
    
    // If no specific campaign is selected, use the top performing one
    if (!selectedAd && topAds.length > 0) {
      selectedAd = topAds[0]; // Use the first (top) ad
    }
    
    console.log('Selected ad for metrics:', selectedAd);
    
    // If we have no data, return empty metrics
    if (!selectedAd && topAds.length === 0) {
      return createInitialMetrics();
    }
    
    // Create metrics from the top ads data
    const metrics = createInitialMetrics();
    
    // Sum up all values if no specific campaign is selected
    if (!selectedAd && topAds.length > 0) {
      // Aggregate data from all top ads
      const totalLeads = topAds.reduce((sum: number, ad: any) => sum + (ad.leads || 0), 0);
      const totalCost = topAds.reduce((sum: number, ad: any) => sum + (ad.totalCost || 0), 0);
      const totalClicks = topAds.reduce((sum: number, ad: any) => sum + (
        ad.impressions ? Math.round(ad.impressions * (ad.ctr || 0) / 100) : 0
      ), 0);
      const totalImpressions = topAds.reduce((sum: number, ad: any) => sum + (ad.impressions || 0), 0);
      
      // Create distributed daily values
      const leadsByDay = distributeValue(totalLeads, 7);
      const costsByDay = distributeValue(totalCost, 7);
      const clicksByDay = distributeValue(totalClicks, 7);
      const impressionsByDay = distributeValue(totalImpressions, 7);
      
      // Update metrics
      metrics.leads = {
        today: leadsByDay[6],
        thisWeek: totalLeads,
        thisMonth: totalLeads * 4,
        total: totalLeads * 4,
        byDay: leadsByDay
      };
      
      metrics.costs = {
        today: costsByDay[6],
        thisWeek: totalCost,
        thisMonth: totalCost * 4,
        total: totalCost * 4,
        byDay: costsByDay
      };
      
      metrics.clicks = {
        today: clicksByDay[6],
        thisWeek: totalClicks,
        thisMonth: totalClicks * 4,
        total: totalClicks * 4,
        byDay: clicksByDay
      };
      
      metrics.impressions = {
        today: impressionsByDay[6],
        thisWeek: totalImpressions,
        thisMonth: totalImpressions * 4,
        total: totalImpressions * 4,
        byDay: impressionsByDay
      };
      
      // Create campaign details
      metrics.campaignDetails = {
        name: "All Campaigns",
        status: "ACTIVE",
        attributionSetting: "default",
        resultType: "leads",
        results: totalLeads,
        reach: totalImpressions,
        views: totalImpressions,
        frequency: topAds.length > 0 ? totalImpressions / topAds.length : 0,
        costPerResult: totalLeads > 0 ? totalCost / totalLeads : 0,
        spentAmount: totalCost,
        qualityRating: "AVERAGE",
        engagementRating: "AVERAGE",
        conversionRating: "AVERAGE",
        impressions: totalImpressions,
        cpm: totalImpressions > 0 ? (totalCost / totalImpressions) * 1000 : 0,
        linkClicks: totalClicks,
        cpc: totalClicks > 0 ? totalCost / totalClicks : 0,
        ctr: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
        allClicks: totalClicks,
        allCtr: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
        allCpc: totalClicks > 0 ? totalCost / totalClicks : 0,
        reportStart: getDateBeforeDays(30),
        reportEnd: new Date().toISOString().split('T')[0],
        roi: 0,
        cpa: totalLeads > 0 ? totalCost / totalLeads : 0,
        conversionRate: totalClicks > 0 ? (totalLeads / totalClicks) * 100 : 0
      };
    } else if (selectedAd) {
      // Calculate clicks since some topAds data might not have clicks directly
      const clicks = selectedAd.ctr 
        ? Math.round((selectedAd.impressions || 0) * (selectedAd.ctr / 100)) 
        : 0;
        
      // Use data from the selected ad
      const leadsByDay = distributeValue(selectedAd.leads || 0, 7);
      const costsByDay = distributeValue(selectedAd.totalCost || 0, 7);
      const clicksByDay = distributeValue(clicks, 7);
      const impressionsByDay = distributeValue(selectedAd.impressions || 0, 7);
      
      // Update metrics for this specific ad
      metrics.leads = {
        today: leadsByDay[6],
        thisWeek: selectedAd.leads || 0,
        thisMonth: (selectedAd.leads || 0) * 4,
        total: (selectedAd.leads || 0) * 4,
        byDay: leadsByDay
      };
      
      metrics.costs = {
        today: costsByDay[6],
        thisWeek: selectedAd.totalCost || 0,
        thisMonth: (selectedAd.totalCost || 0) * 4,
        total: (selectedAd.totalCost || 0) * 4,
        byDay: costsByDay
      };
      
      metrics.clicks = {
        today: clicksByDay[6],
        thisWeek: clicks,
        thisMonth: clicks * 4,
        total: clicks * 4,
        byDay: clicksByDay
      };
      
      metrics.impressions = {
        today: impressionsByDay[6],
        thisWeek: selectedAd.impressions || 0,
        thisMonth: (selectedAd.impressions || 0) * 4,
        total: (selectedAd.impressions || 0) * 4,
        byDay: impressionsByDay
      };
      
      // Create campaign details from the ad
      metrics.campaignDetails = {
        name: selectedAd.name || "Campaign",
        status: selectedAd.status || "ACTIVE",
        attributionSetting: "default",
        resultType: "leads",
        results: selectedAd.leads || 0,
        reach: selectedAd.impressions || 0,
        views: selectedAd.impressions || 0,
        frequency: 1,
        costPerResult: selectedAd.leads > 0 ? selectedAd.totalCost / selectedAd.leads : 0,
        spentAmount: selectedAd.totalCost || 0,
        qualityRating: "AVERAGE",
        engagementRating: "AVERAGE",
        conversionRating: "AVERAGE",
        impressions: selectedAd.impressions || 0,
        cpm: selectedAd.impressions > 0 ? (selectedAd.totalCost / selectedAd.impressions) * 1000 : 0,
        linkClicks: clicks,
        cpc: selectedAd.cpc || 0,
        ctr: selectedAd.ctr || 0,
        allClicks: clicks,
        allCtr: selectedAd.ctr || 0,
        allCpc: selectedAd.cpc || 0,
        reportStart: getDateBeforeDays(30),
        reportEnd: new Date().toISOString().split('T')[0],
        roi: 0,
        cpa: selectedAd.costPerLead || 0,
        conversionRate: clicks > 0 ? (selectedAd.leads / clicks) * 100 : 0
      };
    }
    
    // Store in cache
    metricsCache.set(cacheKey, { 
      data: metrics, 
      timestamp: now 
    });
    
    // Track successful request
    trackSuccessfulRequest('meta_metrics');
    
    return metrics;
  } catch (error: any) {
    console.error('Error fetching metrics data:', error);
    
    // Handle rate limit errors
    if (handleRateLimitError(error)) {
      throw new Error(`Meta API Ratenbegrenzung erreicht. Bitte warten Sie einige Minuten.`);
    }
    
    // Return error to be handled by caller
    throw error;
  }
}

// Helper functions
function getDateBeforeDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
}

// Export the health score calculator as well
export function calculateCampaignHealthScore(campaign: any): number {
  if (!campaign) return 0;
  
  // Define thresholds for metrics
  const thresholds = {
    cpm: { green: 25, yellow: [25, 45], red: 45, higherIsBetter: false },
    cpc: { green: 1.5, yellow: [1.5, 3], red: 3, higherIsBetter: false },
    ctr: { green: 1.5, yellow: [0.8, 1.5], red: 0.8, higherIsBetter: true },
    frequency: { green: [2, 4], yellow: [4, 6], red: 6, higherIsBetter: false }
  };
  
  // Rate metrics based on thresholds
  const rateMetric = (metric: string, value: number): number => {
    if (!value) return 0;
    
    const threshold = thresholds[metric as keyof typeof thresholds];
    if (!threshold) return 0;
    
    // For CTR we store as decimal but thresholds are percentage
    if (metric === 'ctr') value = value * 100;
    
    const { higherIsBetter } = threshold;
    
    // Check green threshold
    if (Array.isArray(threshold.green)) {
      const [min, max] = threshold.green;
      if (value >= min && value <= max) return 100;
    } else {
      if (higherIsBetter && value >= threshold.green) return 100;
      if (!higherIsBetter && value <= threshold.green) return 100;
    }
    
    // Check yellow range
    if (Array.isArray(threshold.yellow)) {
      const [min, max] = threshold.yellow;
      if ((higherIsBetter && value >= min && value < max) ||
          (!higherIsBetter && value > min && value <= max)) {
        return 50;
      }
    }
    
    // Must be red
    return 0;
  };
  
  // Calculate score for each available metric
  let totalScore = 0;
  let metricCount = 0;
  
  // Base metrics available in most campaigns
  const metrics = [
    { name: 'cpm', value: campaign.cpm },
    { name: 'cpc', value: campaign.cpc },
    { name: 'ctr', value: campaign.ctr },
    { name: 'frequency', value: campaign.frequency }
  ];
  
  // Add each metric score
  metrics.forEach(metric => {
    if (metric.value !== undefined) {
      totalScore += rateMetric(metric.name, metric.value);
      metricCount++;
    }
  });
  
  // Add quality rating score if available
  if (campaign.qualityRanking) {
    if (campaign.qualityRanking === 'ABOVE_AVERAGE') {
      totalScore += 100;
    } else if (campaign.qualityRanking === 'AVERAGE') {
      totalScore += 50;
    }
    metricCount++;
  }
  
  // Calculate average score
  return metricCount > 0 ? Math.round(totalScore / metricCount) : 0;
}
